import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { isNil, isEqual } from 'lodash' // Use isEqual to compare lists
import { Grid, Typography, Box, useTheme, useMediaQuery } from '@mui/material'
import { List } from 'components/Listing/List'
import Loader from 'components/Loader'
import { Filters } from '../../components/Filters'
import { FilterType } from '../../components/Filters/filterDefinitions'
import { fetchAllCandidates } from 'src/redux/candidate'
import { applyFilters } from 'components/Filters/helpers'
import PhysicianContent from './PhysicianContent'
import { fetchProviderFilterValues } from 'src/redux/filters'

const Physicians = () => {
  const { signedIn, employerId, employerUserId } = useSelector(state => state.identity)
  const dispatch = useDispatch()
  const theme = useTheme()
  const mobile = useMediaQuery(theme.breakpoints.down('md'))
  const [activeItem, setActiveItem] = useState(null)
  const [page, setPage] = useState(1)
  const pageSize = 50
  const physicianList = useSelector(state => state.candidate.list) || []
  const { isLoadingFilters } = useSelector(state => state.filters).loading
  const filterValues = useSelector(state => state.filters.providerFilterValues) || {}
  const totalCandidates = useSelector(state => state.candidate.totalCandidates)
  const totalPages = Math.ceil(totalCandidates / pageSize)
  const { loading: isLoadingCandidates } = useSelector(state => state.candidate)
  const [searchParams] = useSearchParams()
  const [displayedData, setDisplayedData] = useState([]) // Start with an empty array
  const candidateTypeId = searchParams.get('candidateTypeId')

  // Resizable panel state
  const [leftPanelWidth, setLeftPanelWidth] = useState(() => {
    const saved = localStorage.getItem('physiciansLeftPanelWidth')
    // Default 30% of typical viewport width (assuming ~1400px width = ~420px)
    return saved ? parseInt(saved) : 420
  })
  const [isResizing, setIsResizing] = useState(false)

  const startResize = (e) => {
    if (mobile) return
    setIsResizing(true)
    e.preventDefault()
  }

  useEffect(() => {
    if (!isResizing) return
    
    const handleMouseMove = (e) => {
      requestAnimationFrame(() => {
        const container = document.getElementById('physicians-container')
        if (!container) return
        
        const containerRect = container.getBoundingClientRect()
        const newWidth = e.clientX - containerRect.left
        
        if (newWidth >= 250 && newWidth <= 900) {
          setLeftPanelWidth(newWidth)
        }
      })
    }
    
    const stopResize = () => {
      setIsResizing(false)
      localStorage.setItem('physiciansLeftPanelWidth', leftPanelWidth.toString())
    }
    
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', stopResize)
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', stopResize)
    }
  }, [isResizing, leftPanelWidth, mobile])

  const handleListItemClick = currId => {
    const activePhysician = !isNil(currId)
      ? displayedData.find(p => `${p.id}` === `${currId}`)
      : displayedData.length > 0
        ? displayedData[0]
        : null
    setActiveItem(activePhysician)
  }

  // Fetch provider filter values
  useEffect(() => {
    const specialtyIdsParam = searchParams.get('specialtyIds')
    const specialtyIds = specialtyIdsParam ? specialtyIdsParam.split('|') : []

    const filterValues = {}

    // Only add candidateTypeId if it exists
    if (candidateTypeId) {
      filterValues.candidateTypeId = candidateTypeId
    }

    // Only add specialtyIds if they exist
    if (specialtyIds.length > 0) {
      filterValues.specialtyIds = specialtyIds
    }
    dispatch(fetchProviderFilterValues(filterValues))
  }, [candidateTypeId, searchParams, dispatch])

  useEffect(() => {
    if (!isEqual(displayedData, physicianList)) {
      setDisplayedData(physicianList)
    }
  }, [physicianList, displayedData])

  useEffect(() => {
    if (physicianList.length > 0 && !mobile) {
      setActiveItem(physicianList[0])
    } else if (mobile) {
      setActiveItem(null)
    } else if (physicianList.length === 0) {
      setActiveItem(null)
    }
  }, [physicianList, mobile])

  const leftPanelHeader =
    'Results: ' + `${totalCandidates ? totalCandidates.toLocaleString() : 0} ` + 'Providers'

  const applyCandidateFilters = ({ searchParams }) => {
    applyFilters({
      searchParams,
      employerId,
      employerUserId,
      dispatch,
      getData: fetchAllCandidates,
      page,
      pageSize,
    })
  }

  // Trigger filter application when searchParams change
  useEffect(() => {
    applyCandidateFilters({ searchParams })
  }, [searchParams, page])

  return (
    <>
      <Grid container justifyContent='center'>
        {signedIn && Object.keys(filterValues).length > 0 && (
          <Grid item xs={12}>
            <Filters
              filterType={FilterType.CANDIDATE}
              filterValues={filterValues}
              listType={'provider'}
              loading={isLoadingFilters}
              applyFilters={applyCandidateFilters}
            />
          </Grid>
        )}
        {isLoadingCandidates ? (
          <Loader />
        ) : (
          <Box id="physicians-container" sx={{ 
            display: 'flex',
            width: '100%',
            maxWidth: '100vw',
            overflow: 'hidden',
            px: { xs: 0, md: 4 },
          }}>
            <Grid container item sm={12} direction='row' sx={{ 
              height: '100%',
              flex: { xs: '1', md: `0 0 ${leftPanelWidth}px` },
              width: { xs: '100%', md: 'auto' },
              overflow: 'hidden',
              px: { xs: 0 },
              '& .MuiBox-root': {
                px: { xs: 0, md: 3 }
              }
            }}>
              <Grid item>
                <Typography variant='h6' sx={{ padding: '10px 16px' }}>
                  {leftPanelHeader}
                </Typography>
              </Grid>
              <Grid container alignItems='flex-start' sx={{ maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
                <List
                  setPage={setPage}
                  page={page}
                  data={displayedData}
                  listType={'provider'}
                  isPhysicianList={true}
                  handleListItemClick={handleListItemClick}
                  dataTotal={totalCandidates}
                  totalPages={totalPages}
                  signedIn={signedIn}
                  isLoading={isLoadingFilters}
                  setActiveItem={setActiveItem}
                  setDisplayedData={setDisplayedData}
                />
              </Grid>
            </Grid>
            
            {/* Resizable Divider */}
            <Box
              onMouseDown={startResize}
              sx={{
                display: { xs: 'none', md: 'block' },
                flexShrink: 0,
                width: '4px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '1px',
                  height: '100%',
                  backgroundColor: 'divider',
                },
                '&:hover, &:active': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            />
            
            <Box display={{ xs: 'none', sm: 'none', md: 'contents' }}>
              <Grid
                container
                item
                direction='column'
                sx={{ 
                  maxHeight: 'calc(100vh - 64px)',
                  flex: { xs: '1', md: '1' },
                  pl: { md: 3 },
                  width: { xs: '100%', md: 'auto' },
                  overflow: 'auto',
                  px: { xs: 0 },
                  '& .MuiBox-root': {
                    px: { xs: 0, md: 3 }
                  }
                }}
              >
                {activeItem && <PhysicianContent item={activeItem} signedIn={signedIn} />}
              </Grid>
            </Box>
          </Box>
        )}
      </Grid>
    </>
  )
}

export default Physicians
